/**
 * Atalhos de teclado do loop: espaço (reproduzir/pausar), ←/→ (quadro
 * anterior/próximo), Home/End (primeiro/último).
 *
 * Não interferem no comportamento nativo: campos de texto/seleção e diálogos
 * ficam de fora; em botões e links o espaço continua "clicando" (as setas
 * funcionam); no slider do tempo as setas são do próprio slider.
 *
 * A zona da previsão (à direita da linha do tempo) vê a tecla primeiro
 * (`teclaDaPrevisao`): com o cursor lá, as setas andam pelas antecedências e o
 * espaço toca o fluxo dela; na imagem mais recente, → entra na previsão.
 */
import { useEffect } from 'react';
import { alternarTocar, andarPasso } from '../previsao/motor';
import { emModoPrevisao, usePrevisaoStore } from '../previsao/store';
import { teclaDaPrevisao } from '../tracking/fluxo';
import { goToFirst, goToLast, isExportLocked, stepFrame, togglePlay } from './playback';

type Scope = 'all' | 'arrows' | 'space' | 'none';

/** Quais atalhos valem com o foco neste elemento. */
function shortcutScope(target: EventTarget | null): Scope {
  if (!(target instanceof HTMLElement)) return 'all';
  if (target.isContentEditable || target.closest('[role="dialog"]')) return 'none';
  // Grupos com navegação própria por setas (opções, abas, menus).
  if (target.closest('[role="radiogroup"],[role="tablist"],[role="listbox"],[role="menu"],[role="slider"]')) return 'none';
  const tag = target.tagName;
  if (tag === 'INPUT') return (target as HTMLInputElement).type === 'range' ? 'space' : 'none';
  if (tag === 'SELECT' || tag === 'TEXTAREA') return 'none';
  if (tag === 'BUTTON' || tag === 'A' || target.getAttribute('role')) return 'arrows';
  return 'all';
}

export function useLoopShortcuts(): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || isExportLocked()) return;
      const scope = shortcutScope(e.target);
      if (scope === 'none') return;
      const isSpace = e.key === ' ' || e.key === 'Spacebar';
      // "Próximos dias": as teclas andam pelos passos da previsão, não pelas imagens.
      if (emModoPrevisao()) {
        if (usePrevisaoStore.getState().exportando) return;
        if (isSpace ? scope === 'arrows' : scope === 'space') return;
        if (isSpace) alternarTocar();
        else if (e.key === 'ArrowLeft') andarPasso(-1);
        else if (e.key === 'ArrowRight') andarPasso(1);
        else if (e.key === 'Home') andarPasso(-1_000);
        else if (e.key === 'End') andarPasso(1_000);
        else return;
        e.preventDefault();
        return;
      }
      if (isSpace) {
        if (scope === 'arrows') return;
        if (!teclaDaPrevisao(' ')) togglePlay();
      } else {
        if (scope === 'space') return;
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key) && teclaDaPrevisao(e.key)) {
          // tratada pela zona da previsão
        } else if (e.key === 'ArrowLeft') stepFrame(-1);
        else if (e.key === 'ArrowRight') stepFrame(1);
        else if (e.key === 'Home') goToFirst();
        else if (e.key === 'End') goToLast();
        else return;
      }
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}
